"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderSearch() {
  const [search, setSearch] = useState("");
  const handleSearch = () => {
    if (search.trim()) {
      window.location.href = `/ShopPage?s=${encodeURIComponent(search)}`;
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  return <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="text-primary hover:bg-primary/5 hover:text-primary transition-colors">
          <Search className="size-5" />
          <span className="sr-only">Buscar suplementos</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-4 border-primary/10 shadow-xl bg-background/95 backdrop-blur-md">
        <div className="flex gap-2">
          <ProductsSearch value={search} onChange={setSearch} className="flex-1 border-primary/20 focus-visible:ring-primary" placeholder="Buscar produtos..." />
          <Button type="button" size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-default" onClick={handleSearch}>
            Buscar
          </Button>
        </div>
      </PopoverContent>
    </Popover>;
}
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="202450" className="hidden lg:flex items-center justify-between w-full h-16">
        <div className="flex-1 flex justify-end pr-8 gap-4">
          <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
          <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
        </div>
        <div className="shrink-0 px-4">
          <div className="h-12 w-32 bg-muted animate-pulse rounded"></div>
        </div>
        <div className="flex-1 flex justify-start pl-8 gap-4">
          <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
          <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
        </div>
      </div>;
  }
  const midPoint = Math.ceil(menuItems.length / 2);
  const leftItems = menuItems.slice(0, midPoint);
  const rightItems = menuItems.slice(midPoint);
  return <div className="hidden lg:flex items-center justify-between w-full">
      <div className="flex-1 flex justify-end pr-8">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-8">
            {leftItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="text-sm font-default font-medium tracking-widest text-foreground/70 hover:text-primary transition-colors uppercase">
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      <div className="shrink-0 px-6">
        <Link to="#" className="block transition-transform hover:scale-105 duration-500">
          <WvcLogo className="h-16 md:h-20 w-auto" />
        </Link>
      </div>

      <div className="flex-1 flex justify-start pl-8 items-center">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-8">
            {rightItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="text-sm font-default font-medium tracking-widest text-foreground/70 hover:text-primary transition-colors uppercase">
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>

        <div className="ml-10 flex items-center gap-2 border-l border-primary/10 pl-6">
          <HeaderSearch />
          <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-primary/5 transition-colors" iconSize="size-5" />
        </div>
      </div>
    </div>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <div className="flex lg:hidden items-center justify-between w-full">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-primary/5">
            <Menu className="size-6" />
            <span className="sr-only">Menu de navegação</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[300px] sm:w-[350px] bg-background/98 backdrop-blur-2xl border-r border-primary/10 p-0">
          <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
          <div className="flex flex-col h-full">
            <div className="p-8 border-b border-primary/5 flex justify-center">
              <WvcLogo className="h-14 w-auto" />
            </div>
            
            <div className="flex-1 overflow-y-auto py-8 px-8">
              {loading ? <div className="space-y-6">
                  {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-3/4 bg-muted animate-pulse rounded" />)}
                </div> : <nav className="flex flex-col gap-8">
                  {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-default font-medium tracking-widest text-foreground/80 hover:text-primary transition-colors uppercase" onClick={() => setIsOpen(false)}>
                      {item.label}
                    </Link>)}
                </nav>}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <div className="absolute left-1/2 -translate-x-1/2">
        <Link to="#">
          <WvcLogo className="h-12 w-auto" />
        </Link>
      </div>

      <div className="flex items-center gap-1">
        <HeaderSearch />
        <CartIcon variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-primary/5" iconSize="size-5" />
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="cabeçalho-premium" className={cn("sticky top-0 z-50 w-full transition-all duration-700 ease-in-out bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/10 shadow-sm py-2" : "bg-background border-b border-transparent py-4 md:py-8")}>
      <div className="container mx-auto px-4 md:px-10">
        <MenuProvider menu_id="53">
          <DesktopNav />
          <MobileNav />
        </MenuProvider>
      </div>
    </section>;
}